import { WidthFull } from '@mui/icons-material';
import { React, useState, useEffect, Drawer, DialogActions, Box, Typography, Divider, IconButton, ArrowBackIosIcon, Email_Regex, SelectChangeEvent } from '../../utils/globalImports';
import { CustomButton, CustomTextField, CustomDropdown, CustomRadioButton, useApi, validationMessages } from '../../utils/globalImports';

interface UserData {
  name?: string;
  firstName: string;
  lastName: string;
  email: string;
  roleId: number;
  Status: string;
}

interface CustomAddUserModalProps {
  open: boolean;
  onClose: (closeConfirmationRequired: boolean) => void;
  onSubmit: (formData: UserData) => void;
  userData?: UserData; // Optional prop for editing a user
  handleContentChange: (state: boolean) => void;
}

interface Option {
  value: string | number;
  text: string;
}

const CustomAddUserModal: React.FC<CustomAddUserModalProps> = ({ open, onClose, onSubmit, userData,handleContentChange, }) => {
  const [options, setOptions] = useState<Option[]>([]);
  const { execute } = useApi();

  const [formData, setFormData] = useState<UserData>({
    name: '',
    firstName: '',
    lastName: '',
    email: '',
    roleId: -1,
    Status: 'Active',
  });

  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    roleId: '',
  });


  const validate = () => {
    let isValid = true;
    const errors = { firstName: '', lastName: '', email: '', roleId: '' };

    if (!formData.firstName || !formData.firstName.trim()) {
      errors.firstName = validationMessages.common_required("First name");
      isValid = false;
    }
    else if (/[^a-zA-Z\s]/.test(formData.firstName)) {  // Checks if firstName contains any number
      errors.firstName = validationMessages.common_invalidString("First name")
      isValid = false;
    }

    if (!formData.lastName || !formData.lastName.trim()) {
      errors.lastName = validationMessages.common_required("Last name");
      isValid = false;
    } else if (/[^a-zA-Z\s]/.test(formData.lastName)) {  // Checks if lastName contains any number
      errors.lastName = validationMessages.common_invalidString("Last name")
      isValid = false;
    }

    if (!formData.email) {
      errors.email = validationMessages.common_required("Email");
      isValid = false;
    } else if (!Email_Regex.test(formData.email)) {
      errors.email = validationMessages.common_email("User");
      isValid = false;
    }

    if (formData.roleId == -1) {
      errors.roleId = validationMessages.common_required("Role");
      isValid = false;
    }

    setErrors(errors);
    return isValid;
  };


  const handleSubmit = () => {
    if (validate()) {
      handleContentChange(false);
      onSubmit(formData);
      onClose(false); // Close the drawer after submission
    }
  };

  // Populate the form fields when editing a user
  useEffect(() => {
    fetchRoles(); // Fetch roles on component mount
    if (open) {
      if (userData && userData.name) {
        const [firstName, lastName] = userData.name.split(' ');
        setFormData({
          firstName: firstName || '',
          lastName: lastName || '',
          email: userData.email || '',
          roleId: userData.roleId || -1,
          Status: userData.Status || 'Active',
        });
      } else {
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          roleId: -1,
          Status: 'Active',
        });
      }
      setErrors({
        firstName: '',
        lastName: '',
        email: '',
        roleId: '',
      });
    }

  }, [open, userData]);

  // fetch Role
  const fetchRoles = async () => {
    const { data } = await execute('get', '/User/GetAllRoles');
    if (data && Array.isArray(data)) {
      const formattedOptions = data.map((role: any) => ({
        value: role.roleId,  // Ensure `id` exists in the response
        text: role.roleName, // Ensure `name` exists in the response
      }));

      setOptions(formattedOptions);
    }
  }


  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // For other inputs like text fields, handle them normally
    setFormData({
      ...formData,
      [name]: value,
    });
    handleContentChange(true);
  };

  const handleOnChangeRole = (event: SelectChangeEvent<string | number>) => {
    setFormData({ ...formData, roleId: Number(event.target.value) });
    handleContentChange(true);
  };

  const handleNavigate = () => {
    onClose(true);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => {
        onClose(true);
      }}
      sx={{
        width: 350,  // Set the width of the drawer
        flexShrink: 0,  // Prevent shrinking
        '& .MuiDrawer-paper': {
          width: 500,  // Drawer width
          height: '100%',  // Full height of the screen
          borderRadius: 0, // Optional: To keep sharp edges
          paddingLeft: 2,
          paddingRight: 2,
          paddingTop: 1
        },
      }}
    >
      <Box>
        <Typography
          sx={{
            color: '#1e66ab',
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            mb: 2,
            mt: 2,
            position: 'relative',
            fontWeight: 500,
            fontSize: 22
          }}
        >

          <IconButton
            sx={{
              position: 'absolute',
              left: 0,
              width: '40px',
              height: '40px',
              backgroundColor: 'transparent'
            }}
            onClick={handleNavigate}
          >
            <ArrowBackIosIcon sx={{ color: '#1e66ab', fontSize: '15px' }} />
          </IconButton>

          {userData ? 'Edit User' : 'Add User'}


        </Typography>
        <Divider sx={{ mb: 3, mx: "-15px" }} />

        <Box sx={{ mb: 2 }}>
          <CustomTextField
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            sx={{ mt: 1 }}
            required={true}
          />
          {errors.firstName && (
            <Typography className="validationMessage" role="alert">
              {errors.firstName}
            </Typography>
          )}
        </Box>

        <Box sx={{ mb: 2 }}>
          <CustomTextField
            label="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            sx={{ mt: 1 }}
            required={true}
          />
          {errors.lastName && (
            <Typography className="validationMessage" role="alert">
              {errors.lastName}
            </Typography>
          )}
        </Box>

        <Box sx={{ mb: 2 }}>
          <CustomTextField
            label="Email Address"
            name="email"
            type="email"
            disable={userData ? true : false}
            value={formData.email}
            onChange={handleChange}
            sx={{ mt: 1 }}
            required={true}
          />
          {errors.email && (
            <Typography className="validationMessage" role="alert">
              {errors.email}
            </Typography>
          )}
        </Box>

        <Box sx={{ mb: 2 }}>
          <CustomDropdown
            value={formData.roleId}  // Ensure the value is correctly set to the roleId
            onChange={handleOnChangeRole}
            label="Role"
            options={options}
            requiredLabel={true}
            sx={{ mt: 1, height: '35px', fontSize: '12px' }}
          />
          {errors.roleId && (
            <Typography className="validationMessage" role="alert">
              {errors.roleId}
            </Typography>
          )}
        </Box>

        {userData && (
          <>
            <Typography sx={{ color: '#1e66ab', fontSize: 14, fontWeight: 500, ml: 1, mt: 1 }}>Status</Typography>
            <Box sx={{ display: 'flex' }}> {/* Adjust gap as needed */}
              {/* Custom Radio Buttons */}
              <CustomRadioButton
                label="Active"
                value="Active"
                name="Status"
                checked={formData.Status === 'Active'}
                onChange={handleChange}
                required={true}
              />
              <CustomRadioButton
                label="Inactive"
                value="Inactive"
                name="Status"
                checked={formData.Status === 'Inactive'}
                onChange={handleChange}
                required={true}
              />
            </Box>
          </>
        )}

        <DialogActions sx={{ mt: 15 }}>
          {/* Add User */}
          <CustomButton
            capsule
            sx={{
              mt: 2,
              mb: 0,
              textTransform: 'none',
              width: '60%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              margin: '0 auto', // Centers horizontally if parent has `display: block`
            }}
            onClick={handleSubmit}
          >
            {userData ? 'Update User' : 'Add User'}
          </CustomButton>

        </DialogActions>
      </Box>
    </Drawer>
  );
};

export default CustomAddUserModal;
